import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Loader2, Camera, Film, Video, HelpCircle, AlertTriangle,
  FolderKanban, CalendarDays, Tag, Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { classifyPost } from "@/lib/classify-post.functions";
import { useMediaUploads } from "@/lib/use-media-uploads";
import { MediaAttachments } from "@/components/media-attachments";
import { VIDEO_ACCEPT, MEDIA_LIMITS, formatDuration } from "@/lib/media";

interface Community { id: string; name: string; city: string | null; country: string; }
type Hint = "question" | "emergency" | "project" | "event" | null;

export function CreatePostDialog({
  open, onOpenChange, defaultCommunityId,
}: { open: boolean; onOpenChange: (o: boolean) => void; defaultCommunityId?: string }) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const classify = useServerFn(classifyPost);

  const [content, setContent] = useState("");
  const { items: media, tasks, addFiles, remove: removeMedia, move: moveMedia, clear: clearMedia, busy: uploading } = useMediaUploads("posts");
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState<Hint>(null);

  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locationName, setLocationName] = useState<string>("");
  const [locating, setLocating] = useState(false);

  const [communities, setCommunities] = useState<Community[]>([]);
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<string[]>(
    defaultCommunityId ? [defaultCommunityId] : []
  );

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const recordInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Load joined communities
  useEffect(() => {
    if (!open || !user) return;
    supabase
      .from("community_members")
      .select("community_id, communities(id, name, city, country)")
      .eq("user_id", user.id)
      .then(({ data }) => {
        const list = (data ?? []).map((r: any) => r.communities).filter(Boolean) as Community[];
        setCommunities(list);
      });
  }, [open, user]);

  // Auto-detect location on open (silent — no error if denied)
  useEffect(() => {
    if (!open) return;
    if (coords || locationName) return;
    // seed from profile if available
    if (profile?.city || profile?.country) {
      setLocationName([profile.neighborhood, profile.city, profile.country].filter(Boolean).join(", "));
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setCoords({ lat, lng });
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`,
            { headers: { "Accept-Language": "en" } }
          );
          const j = await r.json();
          const a = j?.address ?? {};
          const friendly = [
            a.suburb || a.neighbourhood || a.city_district,
            a.city || a.town || a.village || a.municipality,
            a.country,
          ].filter(Boolean).join(", ");
          if (friendly) setLocationName(friendly);
        } catch { /* ignore */ }
      },
      () => { /* ignored */ },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60 * 1000 }
    );
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autofocus composer
  useEffect(() => {
    if (open) setTimeout(() => composerRef.current?.focus(), 50);
  }, [open]);

  const reset = () => {
    setContent(""); clearMedia(); setHint(null);
    setSelectedCommunityIds(defaultCommunityId ? [defaultCommunityId] : []);
  };

  const refreshLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setCoords({ lat, lng });
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=14&addressdetails=1`
          );
          const j = await r.json();
          const a = j?.address ?? {};
          const friendly = [
            a.suburb || a.neighbourhood || a.city_district,
            a.city || a.town || a.village || a.municipality,
            a.country,
          ].filter(Boolean).join(", ");
          setLocationName(friendly || `${lat.toFixed(3)}, ${lng.toFixed(3)}`);
        } catch {
          setLocationName(`${lat.toFixed(3)}, ${lng.toFixed(3)}`);
        }
        setLocating(false);
      },
      () => { setLocating(false); toast.error("Couldn't get location"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const pickFiles = (files: FileList | null) => {
    if (!files || !user) return;
    void addFiles(Array.from(files), user.id);
  };

  // Community suggestions: rank by simple keyword overlap with content + city match
  const suggestedCommunities = useMemo(() => {
    if (communities.length === 0) return [];
    const text = content.toLowerCase();
    const scored = communities.map((c) => {
      let score = 0;
      const name = c.name.toLowerCase();
      const city = (c.city || "").toLowerCase();
      if (city && locationName.toLowerCase().includes(city)) score += 3;
      name.split(/\s+/).forEach((w) => { if (w.length > 3 && text.includes(w)) score += 2; });
      return { c, score };
    });
    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((s) => s.c);
  }, [communities, content, locationName]);

  const toggleCommunity = (id: string) => {
    setSelectedCommunityIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!user) return;
    if (!content.trim() && media.length === 0) {
      toast.error("Write something or add media");
      return;
    }
    setSubmitting(true);

    // Classify with AI (with safe fallback inside the serverFn)
    let classification;
    try {
      classification = await classify({ data: { content: content.trim(), locationName } });
    } catch {
      classification = {
        category: "community" as const,
        post_type: "update" as const,
        severity: "info" as const,
        tags: [] as string[],
        title: content.trim().split(/\s+/).slice(0, 12).join(" ").slice(0, 120) || "Update",
      };
    }

    // Apply user hint override
    let post_type = classification.post_type;
    let category = classification.category;
    if (hint === "question") post_type = "question";
    if (hint === "emergency") { post_type = "emergency"; category = "emergency"; }
    if (hint === "project") post_type = "project";
    if (hint === "event") post_type = "announcement";

    const firstImage = media.find((m) => m.kind === "image");
    const primaryCommunityId = selectedCommunityIds[0] ?? null;

    const { data, error } = await supabase
      .from("posts")
      .insert({
        author_id: user.id,
        title: classification.title,
        content: content.trim() || classification.title,
        category,
        post_type,
        community_id: primaryCommunityId,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        location_name: locationName.trim() || null,
        image_url: firstImage?.url ?? null,
        tags: classification.tags,
      })
      .select("id")
      .single();

    if (error) { setSubmitting(false); toast.error(error.message); return; }

    if (data && media.length > 0) {
      const rows = media.map((m, i) => ({
        post_id: data.id,
        url: m.url,
        media_type: m.kind,
        position: i,
        poster_url: m.posterUrl ?? null,
        duration_seconds: m.durationSeconds ?? null,
        byte_size: m.byteSize ?? null,
        storage_bucket: m.storageBucket ?? null,
        storage_path: m.storagePath ?? null,
        poster_path: m.posterPath ?? null,
      }));
      await supabase.from("post_media").insert(rows);
    }

    if (data && post_type === "emergency") {
      await supabase.from("emergency_alerts").insert({
        created_by: user.id,
        post_id: data.id,
        title: classification.title,
        body: content.trim() || classification.title,
        severity: classification.severity === "info" ? "warning" : classification.severity,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        location_name: locationName.trim() || null,
      });
    }

    setSubmitting(false);
    toast.success("Posted");
    onOpenChange(false);
    reset();
    if (data) navigate({ to: "/posts/$id", params: { id: data.id } });
  };

  const quickActions: { key: NonNullable<Hint> | "photo" | "video" | "record" | "location"; label: string; icon: any; onClick: () => void; active?: boolean }[] = [
    { key: "photo", label: "Photo", icon: Camera, onClick: () => photoInputRef.current?.click() },
    { key: "video", label: "Video", icon: Film, onClick: () => videoInputRef.current?.click() },
    { key: "record", label: "Record", icon: Video, onClick: () => recordInputRef.current?.click() },
    { key: "location", label: locationName ? "Location" : "Add location", icon: MapPin, onClick: refreshLocation },
    { key: "question", label: "Ask", icon: HelpCircle, onClick: () => setHint(hint === "question" ? null : "question"), active: hint === "question" },
    { key: "emergency", label: "Emergency", icon: AlertTriangle, onClick: () => setHint(hint === "emergency" ? null : "emergency"), active: hint === "emergency" },
    { key: "event", label: "Event", icon: CalendarDays, onClick: () => setHint(hint === "event" ? null : "event"), active: hint === "event" },
    { key: "project", label: "Project", icon: FolderKanban, onClick: () => setHint(hint === "project" ? null : "project"), active: hint === "project" },
  ];

  const onDropFiles = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!user) return;
    void addFiles(Array.from(e.dataTransfer.files), user.id);
  };

  const canPost = (content.trim().length > 0 || media.length > 0) && !submitting && !uploading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropFiles}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h2 className="font-display text-xl font-bold tracking-tight">What's happening around you?</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Share an update, ask a question, report an issue or rally your community.
          </p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5">
          {/* Location chip */}
          {locationName ? (
            <button
              type="button"
              onClick={refreshLocation}
              className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {locating ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />}
              {locationName}
            </button>
          ) : null}

          {/* Composer */}
          <Textarea
            ref={composerRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's happening nearby?"
            rows={4}
            maxLength={4000}
            className="min-h-[120px] resize-none border-0 bg-transparent px-0 text-lg leading-snug shadow-none placeholder:text-muted-foreground/70 focus-visible:ring-0"
          />

          {/* Hint badge */}
          {hint && (
            <div className={cn(
              "mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium",
              hint === "emergency" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"
            )}>
              <Tag className="h-3 w-3" />
              {hint === "question" && "Posting as a question"}
              {hint === "emergency" && "Posting as an emergency alert"}
              {hint === "event" && "Posting as an event"}
              {hint === "project" && "Posting as a project"}
            </div>
          )}

          {/* Media preview + upload progress */}
          <MediaAttachments items={media} tasks={tasks} onRemove={removeMedia} onMove={moveMedia} className="mt-1 mb-3" />
          {media.some((m) => m.kind === "video") && (
            <p className="mb-3 text-[11px] text-muted-foreground">
              Video plays through a private, short-lived link — up to {MEDIA_LIMITS.video.label} and{" "}
              {formatDuration(MEDIA_LIMITS.video.maxSeconds)} long.
            </p>
          )}

          {/* Suggested communities */}
          {content.trim().length > 6 && suggestedCommunities.length > 0 && (
            <div className="mb-4">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Post also to:</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestedCommunities.map((c) => {
                  const selected = selectedCommunityIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCommunity(c.id)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {selected ? <Check className="h-3 w-3" /> : null}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sticky bottom: actions + Post */}
        <div className="border-t border-border bg-background/95 px-3 pt-2 pb-3 backdrop-blur">
          <div className="mb-2 flex items-center gap-1 overflow-x-auto px-1 pb-1">
            {quickActions.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={a.onClick}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  a.active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-transparent bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <a.icon className="h-3.5 w-3.5" />
                {a.label}
              </button>
            ))}
            <input ref={photoInputRef} hidden type="file" accept="image/*" multiple onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
            <input ref={videoInputRef} hidden type="file" accept={VIDEO_ACCEPT} multiple onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
            <input ref={recordInputRef} hidden type="file" accept="video/*" capture="environment" onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
          </div>
          <div className="flex items-center justify-between gap-3 px-1">
            <span className="text-[11px] text-muted-foreground">
              {content.length > 0 ? `${content.length}/4000` : "Draft auto-saves nothing — keep it short"}
            </span>
            <Button
              onClick={submit}
              disabled={!canPost}
              size="lg"
              className="h-11 min-w-[110px] rounded-full text-base font-semibold"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
